import React, { createContext, useContext, useMemo } from 'react';

import useSessionStorage from './useSessionStorage';

interface IContextProps {
  user_id: string;
  user_data: any;
  userIsAdmin: boolean;
  userIsSuperAdmin: boolean;
  userIsViewer: boolean;
  isCopyBlock: boolean;
  userHasViewAllRole: boolean;
  external_signatory_id: string;
  external_email_id: string;
  external_user_id: string;
  savedSignRefreshToken: string;
  userIsCreator: boolean;
  accessToken: string;
  external_auth_token: string;
}

// Create a context for user data
const UserDataContext = createContext<IContextProps | null>(null);

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }

  const {
    user_data,
    userIsAdmin,
    userIsSuperAdmin,
    userIsViewer,
    isCopyBlock,
    userHasViewAllRole,
    user_id,
    external_signatory_id,
    external_email_id,
    external_user_id,
    savedSignRefreshToken,
    userIsCreator,
    accessToken,
    external_auth_token,
  } = context;

  return {
    user_data,
    userIsAdmin,
    userIsViewer,
    isCopyBlock,
    userHasViewAllRole,
    userIsSuperAdmin,
    user_id,
    external_signatory_id,
    external_email_id,
    external_user_id,
    savedSignRefreshToken,
    userIsCreator,
    accessToken,
    external_auth_token,
  };
};

interface IProps {
  children: React.ReactNode;
}

// Create the provider component
export const UserDataProvider: React.FC<IProps> = ({ children }) => {
  const [user_data] = useSessionStorage<any>('user_profile');
  const [savedSignRefreshToken] = useSessionStorage<any>('signRefreshToken');
  const [accessToken] = useSessionStorage<any>('accessToken');
  const [external_auth_token] = useSessionStorage<any>('external_auth_token');
  const [external_signatory_id] = useSessionStorage<any>(
    'external_signatory_id'
  );
  const [external_email_id] = useSessionStorage<any>('external_email');
  const [external_user_id] = useSessionStorage<any>('external_user_id');

  const userIsAdmin = useMemo(
    () => user_data?.roles?.includes('admin'),
    [user_data?.roles]
  );

  const userIsViewer = useMemo(() => {
    const normalizedRoles = user_data?.roles?.map((role: any) =>
      role?.toLowerCase()
    );
    return normalizedRoles?.includes('viewer');
  }, [user_data?.roles]);

  const isCopyBlock = useMemo(() => {
    const normalizedRoles = user_data?.roles?.map((role: any) =>
      role?.toLowerCase()
    );
    return normalizedRoles?.includes('block copy');
  }, [user_data?.roles]);

  const userHasViewAllRole = useMemo(() => {
    const normalizedRoles = user_data?.roles?.map((role: any) =>
      role?.toLowerCase()
    );
    return normalizedRoles?.includes('view all');
  }, [user_data?.roles]);

  const userIsSuperAdmin = useMemo(
    () => user_data?.roles?.includes('Super Admin'),
    [user_data?.roles]
  );

  const userIsCreator = useMemo(
    () => user_data?.roles?.includes('Creators'),
    [user_data?.roles]
  );

  const value = useMemo(
    () => ({
      user_data,
      userIsAdmin,
      userIsViewer,
      isCopyBlock,
      userHasViewAllRole,
      userIsSuperAdmin,
      user_id: user_data?.id,
      external_signatory_id,
      external_email_id,
      external_user_id,
      savedSignRefreshToken,
      userIsCreator,
      accessToken,
      external_auth_token,
    }),
    [
      user_data,
      accessToken,
      external_auth_token,
      userIsCreator,
      external_email_id,
      external_user_id,
      userIsAdmin,
      userIsViewer,
      isCopyBlock,
      userHasViewAllRole,
      userIsSuperAdmin,
      external_signatory_id,
      savedSignRefreshToken,
    ]
  );

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
